// o2pTouch.js
// ============================================================
// Touch/drag interaction handlers for o2p (object-to-path).
// Provides drag-along-path and rotation handle control for
// trig:touch faders and knobs.
// ============================================================

import {
    computeBBoxCenterScreen,
    repositionRotationRing,
    repositionHitLabelFast
} from "../cues/animOverlays.js";
import { screenSpaceDelta } from "../util/svgCoords.js";
import { mayOperate } from "../cues/controlAccess.js";

window._oscillaDragSessions = window._oscillaDragSessions || new Map();

// ============================================================
// SIMPLE LINE DETECTION & HANDLING
// Browsers have issues with getPointAtLength() on 2-point paths
// (straight lines). We detect these and use direct math instead.
// ============================================================

/**
 * Parse a path to check if it's a simple 2-point line.
 * Returns { x1, y1, x2, y2, length } or null if not a simple line.
 * Caches result on the element to avoid re-parsing.
 *
 * Handles:
 *   - "M x,y L x2,y2" (standard lineto)
 *   - "M x,y x2,y2" (implicit lineto)
 *   - "M x,y V y2" (vertical line)
 *   - "M x,y H x2" (horizontal line)
 */
export function parseSimpleLine(pathEl) {
    // Check cache first
    if (pathEl._simpleLine !== undefined) {
        return pathEl._simpleLine;
    }

    const d = (pathEl.getAttribute("d") || "").trim();
    if (!d) {
        pathEl._simpleLine = null;
        return null;
    }

    let x1, y1, x2, y2;

    // "M/m x,y L/l x2,y2" or "M/m x,y x2,y2" (implicit lineto)
    // Lowercase 'l' or implicit lineto after 'm' = relative coords
    const patternL = /^([Mm])\s*([-\d.eE+]+)[,\s]+([-\d.eE+]+)\s*(?:([Ll])\s*)?([-\d.eE+]+)[,\s]+([-\d.eE+]+)\s*Z?\s*$/;
    let match = d.match(patternL);

    if (match) {
        x1 = parseFloat(match[2]);
        y1 = parseFloat(match[3]);
        const cmd = match[4]; // 'L', 'l', or undefined (implicit)
        const rawX = parseFloat(match[5]);
        const rawY = parseFloat(match[6]);
        // Relative if: explicit 'l', or no command (implicit lineto after 'm')
        const rel = cmd === 'l' || (!cmd && match[1] === 'm');
        x2 = rel ? x1 + rawX : rawX;
        y2 = rel ? y1 + rawY : rawY;
    } else {
        // "M/m x,y V/v y2" — uppercase V = absolute, lowercase v = relative
        const patternV = /^[Mm]\s*([-\d.eE+]+)[,\s]+([-\d.eE+]+)\s*([Vv])\s*([-\d.eE+]+)\s*Z?\s*$/;
        match = d.match(patternV);
        if (match) {
            x1 = parseFloat(match[1]);
            y1 = parseFloat(match[2]);
            x2 = x1;
            const vVal = parseFloat(match[4]);
            y2 = match[3] === 'v' ? y1 + vVal : vVal;
        } else {
            // "M/m x,y H/h x2" — uppercase H = absolute, lowercase h = relative
            const patternH = /^[Mm]\s*([-\d.eE+]+)[,\s]+([-\d.eE+]+)\s*([Hh])\s*([-\d.eE+]+)\s*Z?\s*$/;
            match = d.match(patternH);
            if (match) {
                x1 = parseFloat(match[1]);
                y1 = parseFloat(match[2]);
                y2 = y1;
                const hVal = parseFloat(match[4]);
                x2 = match[3] === 'h' ? x1 + hVal : hVal;
            } else {
                pathEl._simpleLine = null;
                return null;
            }
        }
    }

    const length = Math.hypot(x2 - x1, y2 - y1);
    const line = { x1, y1, x2, y2, length };
    pathEl._simpleLine = line;
    return line;
}

/**
 * Get point at normalized t (0-1) on a path.
 * Handles simple lines directly, falls back to getPointAtLength for complex paths.
 */
export function getPointOnPath(pathEl, t) {
    const line = parseSimpleLine(pathEl);

    if (line) {
        // Simple line: direct interpolation
        return {
            x: line.x1 + (line.x2 - line.x1) * t,
            y: line.y1 + (line.y2 - line.y1) * t
        };
    }

    // Complex path: use browser API
    const length = pathEl.getTotalLength();
    return pathEl.getPointAtLength(t * length);
}

/**
 * Get total length of a path.
 * Handles simple lines directly.
 */
export function getPathLength(pathEl) {
    const line = parseSimpleLine(pathEl);
    if (line) return line.length;
    return pathEl.getTotalLength();
}

/**
 * Find closest t (0-1) on a path to an SVG coordinate.
 * Uses direct projection for simple lines, binary search for complex paths.
 */
export function findClosestTOnPathFast(pathEl, svgX, svgY) {
    const line = parseSimpleLine(pathEl);

    if (line) {
        // Simple line: direct projection onto line segment
        const dx = line.x2 - line.x1;
        const dy = line.y2 - line.y1;
        const len2 = dx * dx + dy * dy;

        if (len2 === 0) return 0;

        // Project point onto line and clamp to [0, 1]
        const t = ((svgX - line.x1) * dx + (svgY - line.y1) * dy) / len2;
        return Math.max(0, Math.min(1, t));
    }

    // Complex path: binary search (existing algorithm)
    const totalLength = pathEl.getTotalLength();
    if (totalLength === 0) return 0;

    const COARSE_STEPS = 50;
    const FINE_STEPS = 20;

    let bestT = 0;
    let bestDist = Infinity;

    // Coarse search
    for (let i = 0; i <= COARSE_STEPS; i++) {
        const t = i / COARSE_STEPS;
        const pt = pathEl.getPointAtLength(t * totalLength);
        const dist = Math.hypot(pt.x - svgX, pt.y - svgY);
        if (dist < bestDist) {
            bestDist = dist;
            bestT = t;
        }
    }

    // Fine search: ternary search on distance around the best coarse
    // sample. A fixed 20-step grid resolved to 1/500 of the path length —
    // several px on a long path, which start:auto turns into a visible
    // offset from the authored position.
    const dist = t => {
        const pt = pathEl.getPointAtLength(t * totalLength);
        return Math.hypot(pt.x - svgX, pt.y - svgY);
    };
    let lo = Math.max(0, bestT - 1 / COARSE_STEPS);
    let hi = Math.min(1, bestT + 1 / COARSE_STEPS);
    for (let i = 0; i < FINE_STEPS; i++) {
        const m1 = lo + (hi - lo) / 3;
        const m2 = hi - (hi - lo) / 3;
        if (dist(m1) < dist(m2)) hi = m2; else lo = m1;
    }
    return (lo + hi) / 2;
}


// ============================================================
// TOUCH/DRAG MODE: Initialize drag handler for o2p elements
// Uses pointer capture for robust event handling
// ============================================================
export function initO2PDragHandler(hitRecord, pathEl, cfg, updatePositionCallback) {
    if (!hitRecord || !hitRecord.hit || !pathEl) {
        console.warn("[hitLabel] initO2PDragHandler: missing required elements");
        return;
    }

    const hit = hitRecord.hit;
    const uid = cfg.uid;

    // Store drag context
    const dragContext = {
        active: false,
        pathEl,
        cfg,
        updatePosition: updatePositionCallback,
        hitRecord,
        pointerId: null
    };

    // ── Double-tap → type a value ─────────────────────────────────────────
    let lastTapTime = 0;

    function showValueInput() {
        document.getElementById('o2p-val-input')?.remove();

        const currentT = cfg._currentT ?? 0;
        const rect = hit.getBoundingClientRect();

        const dialog = document.createElement('div');
        dialog.id = 'o2p-val-input';
        Object.assign(dialog.style, {
            position: 'fixed',
            left: Math.min(rect.left, window.innerWidth - 180) + 'px',
            top: Math.max(4, rect.top - 90) + 'px',
            zIndex: '999999',
            background: 'rgba(18,18,18,0.95)',
            border: '1px solid #555',
            borderRadius: '6px',
            padding: '8px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px',
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#eee',
            boxShadow: '0 2px 10px rgba(0,0,0,0.6)',
            userSelect: 'none',
        });

        // ── top row: label + input ───────────────────────────────────────
        const topRow = document.createElement('div');
        topRow.style.cssText = 'display:flex;align-items:center;gap:6px';

        const lbl = document.createElement('span');
        lbl.textContent = uid + ':';
        lbl.style.cssText = 'opacity:0.55;font-size:11px;white-space:nowrap';

        // Wrap in a <form> so that the virtual keyboard "Done"/"Go"/Enter
        // fires a reliable submit event on all platforms (iOS keydown is unreliable).
        const form = document.createElement('form');
        form.style.display = 'contents';

        const inp = document.createElement('input');
        inp.type = 'number';
        inp.min = '0'; inp.max = '1'; inp.step = '0.001';
        inp.value = currentT.toFixed(3);
        Object.assign(inp.style, {
            width: '72px',
            background: '#111',
            border: '1px solid #666',
            borderRadius: '3px',
            color: '#fff',
            fontFamily: 'monospace',
            fontSize: '14px',
            padding: '3px 5px',
            outline: 'none',
        });

        form.appendChild(inp);
        topRow.appendChild(lbl);
        topRow.appendChild(form);

        // ── preset buttons ───────────────────────────────────────────────
        const presetRow = document.createElement('div');
        presetRow.style.cssText = 'display:flex;gap:5px';

        function commitValue(v) {
            updatePositionCallback(v, v);
            dialog.remove();
        }

        for (const preset of [0, 0.25, 0.5, 0.75, 1]) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.textContent = preset === 0 ? '0' : preset === 1 ? '1' : String(preset);
            Object.assign(btn.style, {
                flex: '1',
                background: '#222',
                border: '1px solid #555',
                borderRadius: '3px',
                color: '#ccc',
                fontFamily: 'monospace',
                fontSize: '12px',
                padding: '4px 2px',
                cursor: 'pointer',
                touchAction: 'manipulation',
            });
            btn.addEventListener('pointerdown', e => e.stopPropagation());
            btn.addEventListener('click', () => commitValue(preset));
            presetRow.appendChild(btn);
        }

        dialog.appendChild(topRow);
        dialog.appendChild(presetRow);
        document.body.appendChild(dialog);
        requestAnimationFrame(() => { inp.focus(); inp.select(); });

        function commit() {
            const v = parseFloat(inp.value);
            if (!isNaN(v)) commitValue(Math.max(0, Math.min(1, v)));
            else dialog.remove();
        }

        // form submit = Enter / Done on all platforms
        form.addEventListener('submit', e => { e.preventDefault(); commit(); });

        // Escape still handled via keydown (no cross-platform issue with Escape)
        inp.addEventListener('keydown', e => {
            if (e.key === 'Escape') { e.preventDefault(); dialog.remove(); }
            e.stopPropagation();
        });

        // Tap outside dismisses without committing
        setTimeout(() => {
            document.addEventListener('pointerdown', function dismiss(e) {
                if (!dialog.contains(e.target)) {
                    dialog.remove();
                    document.removeEventListener('pointerdown', dismiss, true);
                }
            }, { capture: true });
        }, 120);
    }

    // Convert screen coordinates to the path's LOCAL coordinate space.
    // getPointAtLength() returns points in the path's local space,
    // so pointer coords must be converted to the same space.
    // Using pathEl.getScreenCTM() (not svg.getScreenCTM()) accounts for
    // any ancestor transforms (scale, translate, matrix, rotate) on
    // parent groups like mixer1's matrix(1.77,0,0,1.77,521,-894).
    function screenToSVG(screenX, screenY) {
        const svg = pathEl.ownerSVGElement;
        if (!svg) return { x: screenX, y: screenY };

        // svgDelta is cached at drag-start — assumes the score isn't scrolled
        // mid-drag. Fall back to a fresh computation for non-drag callers.
        const { dx, dy } = dragContext.svgDelta || screenSpaceDelta(svg);

        const pt = svg.createSVGPoint();
        pt.x = screenX - dx;
        pt.y = screenY - dy;

        const ctm = pathEl.getScreenCTM();
        if (!ctm) return { x: screenX, y: screenY };

        const inverse = ctm.inverse();
        const svgPt = pt.matrixTransform(inverse);
        return { x: svgPt.x, y: svgPt.y };
    }

    // Map raw t (0-1) to the configured start/end range
    function mapToRange(rawT) {
        const start = cfg.startPos ?? 0;
        const end = cfg.endPos ?? 1;
        // rawT represents position in range, map it to actual path position
        return start + rawT * (end - start);
    }

    // Handle pointer move during drag
    function onPointerMove(e) {
        if (!dragContext.active) return;
        e.preventDefault();

        const svgCoords = screenToSVG(e.clientX, e.clientY);
        const rawT = findClosestTOnPathFast(pathEl, svgCoords.x, svgCoords.y);
        const mappedT = mapToRange(rawT);

        // Call the update callback with the new position
        if (dragContext.updatePosition) {
            dragContext.updatePosition(mappedT, rawT);
        }
    }

    // Handle pointer up - end drag
    function onPointerUp(e) {
        if (!dragContext.active) return;

        dragContext.active = false;
        dragContext.pointerId = null;
        hit.style.cursor = "grab";

        // Release pointer capture
        try {
            hit.releasePointerCapture(e.pointerId);
        } catch (err) {
            // Capture may already be released
        }

        // Dispatch drag end event
        hitRecord.groupEl?.dispatchEvent(
            new CustomEvent("oscilla-drag-end", {
                bubbles: true,
                detail: { uid, kind: "o2p" }
            })
        );
    }

    // Handle pointer cancel (e.g., touch interrupted by system gesture)
    function onPointerCancel(e) {
        if (!dragContext.active) return;

        dragContext.active = false;
        dragContext.pointerId = null;
        hit.style.cursor = "grab";

        // Dispatch drag end event
        hitRecord.groupEl?.dispatchEvent(
            new CustomEvent("oscilla-drag-end", {
                bubbles: true,
                detail: { uid, kind: "o2p" }
            })
        );
    }

    // Handle pointer down - start drag
    function onPointerDown(e) {
        // Only handle primary button (left click / primary touch)
        if (e.button && e.button !== 0) return;
        // allow:/lock: on the fader's own cue (controlAccess.js) — before the
        // double-tap too, which would let a value be typed in
        if (!mayOperate(window.oscillaAnimRegistry?.[uid]?.el ?? hitRecord.groupEl)) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        // Double-tap: show value input instead of dragging
        const now = Date.now();
        if (now - lastTapTime < 300) {
            lastTapTime = 0;
            e.preventDefault();
            e.stopPropagation();
            if (dragContext.active) {
                dragContext.active = false;
                dragContext.pointerId = null;
                hit.style.cursor = 'grab';
                try { hit.releasePointerCapture(e.pointerId); } catch { /* capture already released */ }
            }
            showValueInput();
            return;
        }
        lastTapTime = now;

        e.preventDefault();
        e.stopPropagation();

        dragContext.active = true;
        dragContext.pointerId = e.pointerId;
        dragContext.svgDelta = screenSpaceDelta(pathEl.ownerSVGElement);
        hit.style.cursor = "grabbing";

        // Capture pointer - all subsequent events for this pointer go to hit element
        hit.setPointerCapture(e.pointerId);

        // Dispatch drag start event
        hitRecord.groupEl?.dispatchEvent(
            new CustomEvent("oscilla-drag-start", {
                bubbles: true,
                detail: { uid, kind: "o2p" }
            })
        );

        // Immediately update position to where user clicked
        onPointerMove(e);

    }

    // Set up visual indication that this is draggable
    hit.style.cursor = "grab";
    // Prevent browser from interpreting touch as scroll/pan gesture (which cancels pointer capture)
    hit.style.touchAction = "none";

    // Attach pointer event listeners to the hit area
    // With pointer capture, all events come to this element - no document listeners needed
    hit.addEventListener("pointerdown", onPointerDown);
    hit.addEventListener("pointermove", onPointerMove);
    hit.addEventListener("pointerup", onPointerUp);
    hit.addEventListener("pointercancel", onPointerCancel);

    // Store the drag context for potential cleanup
    window._oscillaDragSessions.set(uid, dragContext);

    
    return dragContext;
}

// ============================================================
// ROTATION RING: auto-generated HTML/SVG rotation indicator
// ============================================================
// Creates a ring overlay around the fader's hit label with a
// draggable indicator dot. Returns { dot, hit, radius } matching
// the shape expected by updateRotationIndicator() in o2p.js.
//
// Architecture:
//   - HTML div container (position:fixed, follows fader via updateHitCircle)
//   - Inline SVG inside container: ring outline + dot circle

// ============================================================

export function createRotationRing(hitRecord, hmode, rotrange) {
    if (!hitRecord || !hitRecord.groupEl) return null;

    // Size ring relative to fader's actual screen size
    const faderBox = hitRecord.groupEl.getBoundingClientRect();
    const faderR = Math.max(faderBox.width, faderBox.height) / 2;

    const RADIUS = faderR + 14;       // ring orbit: outside the fader + gap
    const DOT_R  = 5;                 // indicator dot radius
    const PAD    = DOT_R + 4;         // padding around ring for dot overflow
    const SIZE   = (RADIUS + PAD) * 2;
    const HALF   = SIZE / 2;
    // Inner hole = fader area, so fader clicks pass through
    const INNER_HOLE = Math.max(14, faderR + 4);
    const svgNS  = "http://www.w3.org/2000/svg";

    // ── Container div (holds the inline SVG) ──────────────────
    const container = document.createElement("div");
    container.className = "oscilla-rotation-ring";
    container.dataset.uid = hitRecord.uid;
    Object.assign(container.style, {
        position: "fixed",
        width:  `${SIZE}px`,
        height: `${SIZE}px`,
        pointerEvents: "none",
        zIndex: "999998"
    });

    // ── Inline SVG ────────────────────────────────────────────
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("width", SIZE);
    svg.setAttribute("height", SIZE);
    svg.setAttribute("viewBox", `${-HALF} ${-HALF} ${SIZE} ${SIZE}`);
    svg.style.overflow = "visible";

    // Ring outline
    const ring = document.createElementNS(svgNS, "circle");
    ring.setAttribute("cx", 0);
    ring.setAttribute("cy", 0);
    ring.setAttribute("r", RADIUS);
    ring.setAttribute("fill", "none");
    ring.setAttribute("stroke", "#ff9933");
    ring.setAttribute("stroke-width", "1.5");
    ring.setAttribute("opacity", "0.6");
    if (hmode === "limited") {
        // Visual distinction for limited-range mode
        ring.setAttribute("stroke-dasharray", "4 2");
    }
    svg.appendChild(ring);

    // Arc range markers for limited mode
    if (hmode === "limited" && rotrange) {
        const range = rotrange || 270;
        // Draw start and end tick marks
        // Zero is at 7-o'clock (120deg standard), range goes clockwise
        for (const angleDeg of [0, range]) {
            const stdAngle = angleDeg + 120;
            const rad = (stdAngle * Math.PI) / 180;
            const inner = RADIUS - 5;
            const outer = RADIUS + 5;
            const tick = document.createElementNS(svgNS, "line");
            tick.setAttribute("x1", Math.cos(rad) * inner);
            tick.setAttribute("y1", Math.sin(rad) * inner);
            tick.setAttribute("x2", Math.cos(rad) * outer);
            tick.setAttribute("y2", Math.sin(rad) * outer);
            tick.setAttribute("stroke", "#ff9933");
            tick.setAttribute("stroke-width", "2");
            tick.setAttribute("opacity", "0.8");
            svg.appendChild(tick);
        }
    }

    // Indicator dot — positioned by updateRotationIndicator() in o2p.js
    // Initial position: 7-o'clock (0 in our system = 120deg standard)
    const initRad = (120 * Math.PI) / 180;
    const dot = document.createElementNS(svgNS, "circle");
    dot.setAttribute("cx", Math.cos(initRad) * RADIUS);
    dot.setAttribute("cy", Math.sin(initRad) * RADIUS);
    dot.setAttribute("r", DOT_R);
    dot.setAttribute("fill", "#ff9933");
    dot.setAttribute("stroke", "#fff");
    dot.setAttribute("stroke-width", "1");
    svg.appendChild(dot);

    container.appendChild(svg);

    // ── Hit area (donut shape — catches pointer events on the ring zone only) ──
    // z-index ABOVE the fader hit (999999) so rotation ring is reachable.
    // clip-path cuts a circular hole in the center so clicks there fall
    // through naturally to the fader hit beneath — no synthetic events needed.
    const hit = document.createElement("div");
    hit.className = "oscilla-rotation-ring-hit";
    hit.dataset.uid = hitRecord.uid;

    Object.assign(hit.style, {
        position: "fixed",
        width:  `${SIZE}px`,
        height: `${SIZE}px`,
        marginLeft: `-${HALF}px`,
        marginTop:  `-${HALF}px`,
        pointerEvents: "auto",
        cursor: "grab",
        zIndex: "1000000",
        borderRadius: "50%",
        background: "transparent",
        // Donut clip: SVG path with outer rect + inner circle cutout (evenodd)
        clipPath: `path(evenodd, 'M 0 0 L ${SIZE} 0 L ${SIZE} ${SIZE} L 0 ${SIZE} Z M ${HALF} ${HALF - INNER_HOLE} A ${INNER_HOLE} ${INNER_HOLE} 0 1 0 ${HALF} ${HALF + INNER_HOLE} A ${INNER_HOLE} ${INNER_HOLE} 0 1 0 ${HALF} ${HALF - INNER_HOLE} Z')`
    });

    document.body.appendChild(container);
    document.body.appendChild(hit);

    // ── Ring data object ──────────────────────────────────────
    const ringData = {
        container,
        hit,
        dot,
        radius: RADIUS,
        uid: hitRecord.uid
    };

    // Store on hit record for automatic repositioning in updateHitCircle
    hitRecord._rotationRing = ringData;

    // Initial position
    repositionRotationRing(hitRecord, ringData);

    return ringData;
}

// Works in screen coordinates — both the fader center (from
// getBoundingClientRect) and pointer events use screen/client space,
// so no SVG coordinate conversion needed. Angle convention matches
// controlXY: 0 = 7 o'clock (120deg standard), increasing clockwise.
// ============================================================

export function initO2PRotationDragHandler(
    rotDragTarget, hitRecord, pathEl, cfg, onRotateCallback
) {
    if (!rotDragTarget) {
        console.warn("[hitLabel] initO2PRotationDragHandler: no drag target");
        return null;
    }

    const dragContext = {
        active: false,
        uid: cfg?.uid || hitRecord?.uid,
        pointerId: null
    };

    // Get fader's current screen center for angle computation
    function getFaderScreenCenter() {
        if (hitRecord?.groupEl) {
            return computeBBoxCenterScreen(hitRecord.groupEl);
        }
        return null;
    }

    function onPointerMove(e) {
        if (!dragContext.active) return;
        e.preventDefault();

        const center = getFaderScreenCenter();
        if (!center) return;

        const dx = e.clientX - center.x;
        const dy = e.clientY - center.y;

        // atan2 → standard math angle, then convert to 7-o'clock-zero
        // Same convention as controlXY lines 715-720
        let angle = Math.atan2(dy, dx) * (180 / Math.PI);
        angle = ((angle - 120) + 360) % 360;

        if (onRotateCallback) {
            onRotateCallback(angle);
        }
    }

    function onPointerUp(e) {
        if (!dragContext.active) return;
        dragContext.active = false;
        dragContext.pointerId = null;
        rotDragTarget.style.cursor = "grab";

        // Release pointer capture
        rotDragTarget.releasePointerCapture(e.pointerId);
    }

    function onPointerCancel(e) {
        if (!dragContext.active) return;
        dragContext.active = false;
        dragContext.pointerId = null;
        rotDragTarget.style.cursor = "grab";
    }

    function onPointerDown(e) {
        if (e.button && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        dragContext.active = true;
        dragContext.pointerId = e.pointerId;
        rotDragTarget.style.cursor = "grabbing";

        // Capture pointer - all subsequent events for this pointer go to rotDragTarget
        rotDragTarget.setPointerCapture(e.pointerId);

        // Immediately compute angle at click position
        onPointerMove(e);
    }

    rotDragTarget.style.cursor = "grab";

    // Attach pointer event listeners - with capture, no document listeners needed
    rotDragTarget.addEventListener("pointerdown", onPointerDown);
    rotDragTarget.addEventListener("pointermove", onPointerMove);
    rotDragTarget.addEventListener("pointerup", onPointerUp);
    rotDragTarget.addEventListener("pointercancel", onPointerCancel);

    // Store for potential cleanup
    window._oscillaDragSessions.set(
        `rot:${dragContext.uid}`, dragContext
    );

    return dragContext;
}


// ============================================================
// PAUSE-DRAG: Free drag when o2p animation is paused
// ============================================================
// When an o2p animation is paused (via click-to-stop), this enables
// unconstrained dragging of the source marker within the spatial
// bounds. During drag, spatial OSC is emitted continuously.
// On resume, findClosestTOnPathFast() locates the closest point on
// the path so animation can restart from the new position.
//
// Unlike touch-mode drag (path-constrained), pause-drag is FREE —
// the source can go anywhere within bounds, just like controlXY.
// ============================================================

window._oscillaPauseDragSessions = window._oscillaPauseDragSessions || new Map();

/**
 * Enable free drag on a paused o2p element.
 * @param {SVGElement} el — the o2p source element (or its wrapper)
 * @param {Object} cfg — the o2p config object
 * @param {Object} callbacks — { onDrag(svgX, svgY), onDragEnd(svgX, svgY) }
 */
export function enableO2PPauseDrag(el, cfg, callbacks = {}) {
    const uid = cfg.uid;
    // A pause while one is already on (paused twice, or a resume that never
    // disabled it): the earlier handler set goes, so one press has one answer.
    disableO2PPauseDrag(uid);
    const wrapper = cfg._wrapper || el;
    const svg = el.ownerSVGElement || document.querySelector("svg");
    if (!svg) { console.warn(`[o2p:pauseDrag] no SVG for ${uid}`); return null; }

    // Find the hit label for this element (the clickable overlay)
    const hitRecord = window._oscillaHitLabels?.find(r => r.uid === uid);
    const dragTarget = hitRecord?.hit || wrapper;

    // Resolve spatial bounds for clamping (dome/plan circle only).
    // If no spatial bounds set, drag is completely unconstrained —
    // detaching from the path is an intentional feature.
    let boundsRect = null;
    if (cfg._spatialBounds) {
        const b = cfg._spatialBounds;
        boundsRect = { cx: b.cx, cy: b.cy, radius: b.radius };
    }

    // Track current position (starts at wherever the animation stopped)
    const bbox = wrapper.getBBox();
    const origCenter = wrapper._o2pOriginalCenter || { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height / 2 };
    let curX = origCenter.x;
    let curY = origCenter.y;

    // Try to get actual position from the wrapper transform
    const existingTransform = wrapper.getAttribute("transform") || "";
    const translateMatch = existingTransform.match(/translate\(\s*([-\d.]+)[,\s]+([-\d.]+)/);
    if (translateMatch) {
        curX = origCenter.x + parseFloat(translateMatch[1]);
        curY = origCenter.y + parseFloat(translateMatch[2]);
    }

    const dragContext = {
        active: false,
        uid,
        curX,
        curY,
        wrapper,
        dragTarget,
        cfg,
        enabled: true,
        startScreenX: 0,
        startScreenY: 0,
        didMove: false,
        pointerId: null
    };

    function screenToSVG(screenX, screenY) {
        const { dx, dy } = dragContext.svgDelta || screenSpaceDelta(svg);

        const pt = svg.createSVGPoint();
        pt.x = screenX - dx;
        pt.y = screenY - dy;

        // Use the wrapper's parent CTM — this is the coordinate space
        // that the wrapper's translate attribute operates in, and the
        // same space as _o2pOriginalCenter and _spatialBounds.
        const ctm = (wrapper.parentNode || svg).getScreenCTM();
        if (!ctm) return { x: screenX, y: screenY };
        return pt.matrixTransform(ctm.inverse());
    }

    function clampToBounds(x, y) {
        if (!boundsRect) return { x, y };

        if (boundsRect.radius) {
            // Circular bounds — clamp to circle
            const dx = x - boundsRect.cx;
            const dy = y - boundsRect.cy;
            const dist = Math.hypot(dx, dy);
            if (dist > boundsRect.radius) {
                const scale = boundsRect.radius / dist;
                return { x: boundsRect.cx + dx * scale, y: boundsRect.cy + dy * scale };
            }
        }
        return { x, y };
    }

    function applyFreePosition(x, y) {
        const clamped = clampToBounds(x, y);
        dragContext.curX = clamped.x;
        dragContext.curY = clamped.y;

        // Compute offset from original center (matches o2p's captureOriginalCenter)
        const origCenter = wrapper._o2pOriginalCenter || { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height / 2 };
        const offX = clamped.x - origCenter.x;
        const offY = clamped.y - origCenter.y;


        wrapper.setAttribute("transform", `translate(${offX}, ${offY})`);

        repositionHitLabelFast(uid);

        // Callback for spatial OSC emission
        if (callbacks.onDrag) {
            callbacks.onDrag(clamped.x, clamped.y);
        }
    }

    // Movement threshold: only start actual drag after pointer moves 5px
    const DRAG_THRESHOLD = 5;

    function onPointerMove(e) {
        if (!dragContext.active || !dragContext.enabled) return;
        e.preventDefault();

        // Check if we've moved enough to start dragging
        if (!dragContext.didMove) {
            const dx = e.clientX - dragContext.startScreenX;
            const dy = e.clientY - dragContext.startScreenY;
            if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
            dragContext.didMove = true;
            dragTarget.style.cursor = "grabbing";
        }

        const svgCoords = screenToSVG(e.clientX, e.clientY);
        applyFreePosition(svgCoords.x, svgCoords.y);
    }

    function onPointerUp(e) {
        if (!dragContext.active) return;
        dragContext.active = false;
        dragContext.pointerId = null;
        dragTarget.style.cursor = "grab";

        // Release pointer capture
        dragTarget.releasePointerCapture(e.pointerId);

        // Only fire drag-end callback if there was actual movement
        if (dragContext.didMove && callbacks.onDragEnd) {
            callbacks.onDragEnd(dragContext.curX, dragContext.curY);
        }

        if (dragContext.didMove) {
            // Suppress the next click so the armed handler doesn't
            // immediately resume. The user can click again to resume.
            const suppress = (ev) => {
                ev.stopImmediatePropagation();
                ev.preventDefault();
                dragTarget.removeEventListener("click", suppress, true);
            };
            dragTarget.addEventListener("click", suppress, true);
            setTimeout(() => dragTarget.removeEventListener("click", suppress, true), 400);

        }
        // If !didMove (just a tap), click passes through → triggers resume
    }

    function onPointerCancel(e) {
        if (!dragContext.active) return;
        dragContext.active = false;
        dragContext.pointerId = null;
        dragTarget.style.cursor = "grab";
    }

    function onPointerDown(e) {
        if (!dragContext.enabled) return;
        if (e.button && e.button !== 0) return;
        // Don't stopPropagation — let click handler still work for resume
        e.preventDefault();

        dragContext.active = true;
        dragContext.didMove = false;
        dragContext.pointerId = e.pointerId;
        dragContext.startScreenX = e.clientX;
        dragContext.startScreenY = e.clientY;
        dragContext.svgDelta = screenSpaceDelta(svg);

        // Capture pointer - all subsequent events for this pointer go to dragTarget
        dragTarget.setPointerCapture(e.pointerId);
    }

    // Visual feedback — change cursor
    dragTarget.style.cursor = "grab";
    dragTarget._pauseDragDown = onPointerDown;

    // Attach pointer event listeners - with capture, no document listeners needed
    dragTarget.addEventListener("pointerdown", onPointerDown);
    dragTarget.addEventListener("pointermove", onPointerMove);
    dragTarget.addEventListener("pointerup", onPointerUp);
    dragTarget.addEventListener("pointercancel", onPointerCancel);

    // Store reference for cleanup
    dragTarget._pauseDragHandlers = { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };

    // Store session
    window._oscillaPauseDragSessions.set(uid, dragContext);

    // Update overlay
    if (cfg._overlay) {
        cfg._overlay.update("⏸ drag to reposition");
    }

    return dragContext;
}


/**
 * Disable free drag on a paused o2p element.
 * Returns the last drag position { x, y } for nearest-t lookup.
 */
export function disableO2PPauseDrag(uid) {
    const ctx = window._oscillaPauseDragSessions.get(uid);
    if (!ctx) return null;

    ctx.enabled = false;
    ctx.active = false;

    // Remove listeners from the target they were put on
    const dragTarget = ctx.dragTarget;

    if (dragTarget._pauseDragHandlers) {
        const h = dragTarget._pauseDragHandlers;
        dragTarget.removeEventListener("pointerdown", h.onPointerDown);
        dragTarget.removeEventListener("pointermove", h.onPointerMove);
        dragTarget.removeEventListener("pointerup", h.onPointerUp);
        dragTarget.removeEventListener("pointercancel", h.onPointerCancel);
        delete dragTarget._pauseDragHandlers;
    }
    delete dragTarget._pauseDragDown;
    dragTarget.style.cursor = "";

    const lastPos = { x: ctx.curX, y: ctx.curY };

    window._oscillaPauseDragSessions.delete(uid);

    return lastPos;
}
